/**
 * Modal that collects a new card via Stripe PaymentElement and attaches it as
 * the customer's default payment method. On success it calls `onSuccess`;
 * cancelling or closing calls `onCancel` (the caller treats that as "aborted").
 *
 * Ported from the deerlake billing flow (AddPaymentMethodDialog).
 */
import { useState, type FormEvent } from 'react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripePaymentElementOptions } from '@stripe/stripe-js';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { Modal } from '@/components/Modal/Modal';
import { Row } from '@/components/SettingsLayout';
import { Button } from '@/ui';
import { updateDefaultPaymentMethod } from '@/utils/helpers/stripeServices';
import { StripeElementsProvider } from './StripeElementsProvider';

type Props = {
  open: boolean;
  companyId: string;
  stripeCustomerId: string;
  /** Receives the id of the newly-added (and now-default) payment method. */
  onSuccess: (paymentMethodId: string) => void;
  onCancel: () => void;
};

export const AddPaymentMethodModal = ({
  open,
  companyId,
  stripeCustomerId,
  onSuccess,
  onCancel,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Modal open={open} closeModal={onCancel} closeOnOverlayClick={false}>
      <Modal.Title title={t('Companies.deferred_fees_add_card_title')} />
      <Modal.Content>
        {open && (
          <StripeElementsProvider stripeCustomerId={stripeCustomerId}>
            <PaymentForm
              companyId={companyId}
              stripeCustomerId={stripeCustomerId}
              onSuccess={onSuccess}
              onCancel={onCancel}
            />
          </StripeElementsProvider>
        )}
      </Modal.Content>
    </Modal>
  );
};

type PaymentFormProps = Omit<Props, 'open'>;

const PaymentForm = ({ companyId, stripeCustomerId, onSuccess, onCancel }: PaymentFormProps) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);

  const paymentElementOptions: StripePaymentElementOptions = { layout: 'tabs' };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsLoading(true);
    try {
      const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });

      if (error) {
        toast.error(error.message || t('Companies.deferred_fees_card_error'));
        return;
      }

      if (setupIntent?.payment_method) {
        const paymentMethodId =
          typeof setupIntent.payment_method === 'string'
            ? setupIntent.payment_method
            : setupIntent.payment_method.id;

        await updateDefaultPaymentMethod(companyId, stripeCustomerId, paymentMethodId);
        toast.success(t('Companies.deferred_fees_card_success'));
        onSuccess(paymentMethodId);
      }
    } catch {
      toast.error(t('Companies.deferred_fees_card_error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className='flex w-full flex-col gap-4'>
      <PaymentElement options={paymentElementOptions} />
      <Row justify='justify-end' gapSize='8'>
        <Button type='button' variant='outline' onClick={onCancel} disabled={isLoading}>
          {t('Shared.cancel')}
        </Button>
        <Button type='submit' variant='primary' disabled={isLoading || !stripe || !elements}>
          {t('Companies.deferred_fees_add_card')}
        </Button>
      </Row>
    </form>
  );
};
